import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';

import { useApp } from 'contexts/AppContext';
import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import pluralize from 'utils/pluralize';
import { ACTIVATION, GROUP, INSIGHT, INSIGHT_SHOPS } from 'utils/rest';

import { Text, Tooltip } from 'components/DataDisplay';
import { MultipleSelectSearch, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import ModalDelete from 'components/ModalDelete';
import { TableData } from 'components/TableData';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import { ModalForm } from './components/ModalForm';

const ShopContainer = () => {
  let timer;
  const [medias, setMedias] = useState([]);
  const [rows, setRows] = useState([]);
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [openModal, setModal] = useState(null);
  const [selected, setSelected] = useState({});
  const [shopGroups, setShopGroups] = useState([]);
  const [selectedShopGroups, setSelectedShopGroups] = useState([]);
  const { themeColors } = useTheme();
  const { adminRights, user } = useUser();
  const { domainConfig, isSimplify } = useApp();
  const { notify } = useNotif();

  const closeModal = () => {
    setModal(null);
    setSelected({});
  };

  const selectShop = async id => {
    const shop = await ACTIVATION.getActivationShop(id);
    setSelected(shop);

    return shop;
  };

  const getToolTipText = txt => {
    return (
      <Row spacing={0}>
        <Item>
          <Text color={themeColors.dark}>{txt}</Text>
        </Item>
      </Row>
    );
  };

  const getDatas = async () => {
    const [shopGroupsList, mediasList] = await Promise.all([
      ACTIVATION.getActivationShopGoups(),
      ACTIVATION.getActivableMedias()
    ]);

    setShopGroups(shopGroupsList);
    setMedias(mediasList);

    setLoaded(true);
  };

  const getShops = async () => {
    try {
      const payload = {
        limit,
        page: pageNumber + 1,
        search: searchFieldValue,
        withActivationShopGroups: true
      };

      if (selectedShopGroups.length > 0) {
        payload.activationShopGroups = selectedShopGroups.map(
          ({ value }) => value
        );
      }

      const {
        results,
        totalCount
      } = await ACTIVATION.getActivationShopsPaginated(payload);
      setTotal(totalCount);
      return results;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const loadShops = async () => {
    const newShops = await getShops();

    const mapped = newShops.map(item => {
      const newItem = { ...item };
      return newItem;
    }, []);
    setRows(mapped);
  };

  const updateGroup = async (data, activationShopId) => {
    const group = await GROUP.getGroupByShop({
      domain: user.domain,
      name: data.name,
      activationShopId
    });

    if (group) {
      await GROUP.updateV2(group._id, {
        userIds: data.users,
        activationShopId
      });
    }
  };
  const updateShopGroups = async (shopId, shopGroupList) => {
    const oldShopGroups = shopGroups.filter(el => el.shops.includes(shopId));
    const toAdd = shopGroupList.filter(
      el => !oldShopGroups.some(old => old._id === el.value)
    );
    const toRemove = oldShopGroups.filter(
      old => !shopGroupList.some(el => old._id === el.value)
    );

    if (toAdd.length > 0) {
      await Promise.all(
        toAdd.map(shopGroup =>
          ACTIVATION.addShopIdsToShopGoup(shopGroup.value, shopId, true)
        )
      );
    }

    if (toRemove.length > 0) {
      await Promise.all(
        toRemove.map(shopGroup =>
          ACTIVATION.removeShopIdsToShopGoup(shopGroup._id, shopId)
        )
      );
    }
  };

  const convertTime = ({ time, unit }) => {
    let val = 0;
    if (unit === 'min') {
      val = parseFloat(time);
    } else {
      val = parseFloat(time) * 60;
    }
    // if the time is equal to 120 minute then 119 minutes is registered in db
    return val === 120 ? 119 : val;
  };

  const generateParametersAndPolygons = async ({
    audienceType,
    parameters
  }) => {
    switch (audienceType) {
      case 'isochrony': {
        return {
          parameters: {
            transportation: parameters.transportation,
            unit: 'min',
            // doit absolument etre en minute pour que le compute fonctionne
            time: convertTime(parameters)
          },
          audienceType,
          polygons: []
        };
      }
      case 'radius': {
        return { parameters, audienceType, polygons: [] };
      }
      default: {
        const rslt = await INSIGHT.getGeoZone({
          searchFields: parameters.map(el => ({
            type: el.type,
            searchField: el.key
          })),
          geo: true
        });

        const polygons = rslt.map(geoZone => ({
          ...geoZone.geoShape
        }));
        return {
          audienceType,
          polygons,
          parameters
        };
      }
    }
  };

  const updateInsigtShop = async ({
    original: insigtShop,
    lat,
    lng,
    address,
    zdc
  }) => {
    if (
      lat !== insigtShop.origin.coordinates[1] ||
      lng !== insigtShop.origin.coordinates[0] ||
      zdc.audienceType !== insigtShop.audienceType ||
      zdc.parameters !== insigtShop.parameters
    ) {
      const origin = { coordinates: [lng, lat], type: 'Point' };
      const params = await generateParametersAndPolygons(zdc);
      await INSIGHT_SHOPS.update(insigtShop._id, {
        origin,
        address,
        ...params
      });
      if (zdc.audienceType === 'isochrony') {
        await INSIGHT_SHOPS.computeInsightShop(insigtShop._id);
      }
    }
  };

  const openUpdate = async id => {
    if (adminRights?.pml?.shop?.update || isSimplify) {
      await selectShop(id);
      setModal('modalUpdate');
    } else {
      setModal('unauthorized');
    }
  };

  const onUpdate = async ({ insigtShop, ...data }, newShopGroups) => {
    const rslt = await ACTIVATION.updateActivationShop(selected._id, {
      ...selected,
      ...data
    });
    if (rslt._id) {
      await updateInsigtShop(insigtShop);
      await updateShopGroups(rslt._id, newShopGroups);
      await updateGroup(data, selected._id);
    }
    closeModal();
    setLoaded(false);
  };

  const openDelete = async id => {
    if (adminRights?.pml?.shop?.delete || isSimplify) {
      await selectShop(id);
      setModal('modalDelete');
    } else {
      setModal('unauthorized');
    }
  };

  const onDelete = async () => {
    const deleted = await ACTIVATION.deleteActivationShop(selected.id);
    closeModal();
    setLoaded(false);
    if (!deleted) {
      notify({
        message:
          'Ce magasin n`est pas supprimable car il est lié à une campagne validée, active, en pause, terminée ou annulée.',
        variant: 'error',
        time: 8000
      });
    }
  };

  const formatShopGroups = (list = [], type, color) => {
    const count = list.length;
    if (count === 0) {
      return <Text>Aucun</Text>;
    }
    if (count === 1) {
      return <Text color={color}> {list[0]} </Text>;
    }

    const others = count - 1;
    const str = ` ${others} ${pluralize('autre', others)} ${pluralize(
      type,
      others
    )}`;

    const tooltipContent = () => {
      return <Container>{list.map(item => getToolTipText(item))}</Container>;
    };
    return (
      <Container>
        <Row spacing={2}>
          <Item flex>
            <Text color={color}> {list[0]}&nbsp;</Text>
          </Item>
          <Item flex>
            <Text> et&nbsp;</Text>
          </Item>
          <Item flex>
            <Tooltip title={tooltipContent()}>
              <Text color={color} link>
                {str}
              </Text>
            </Tooltip>
          </Item>
        </Row>
      </Container>
    );
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (loaded) {
      clearTimeout(timer);
      // eslint-disable-next-line
      timer = setTimeout(loadShops, 500);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [loaded, pageNumber, limit, searchFieldValue, selectedShopGroups]);

  const shopGroupsWithShopsOptions = useMemo(() => {
    const options = shopGroups.reduce((accumulator, shopGroup) => {
      if ((shopGroup.shops || []).length > 0) {
        accumulator.push({
          label: shopGroup.name,
          value: shopGroup._id
        });
      }
      return accumulator;
    }, []);

    return options.sort((leftOption, rightOption) =>
      leftOption.label.localeCompare(rightOption.label)
    );
  }, [shopGroups]);

  const { shopGroupName, shopName } = domainConfig.groupTaxonomy?.reduce(
    (acc, item) => {
      if (item.level === 'shop') {
        acc.shopName = item.name;
      } else if (Number(acc?.shopGroupRef?.level || 0) <= Number(item.level)) {
        acc.shopGroupRef = item;
        acc.shopGroupName = item.name;
      }
      return acc;
    },
    {
      shopGroupRef: null,
      shopGroupName: 'Groupe de magasins',
      shopName: 'Magasins'
    }
  );

  return (
    <Container>
      <Row spacing={4} justify="flex-start">
        <Item xs={3}>
          <TextField
            onChange={e => {
              setPageNumber(0);
              setSearchFieldValue(e);
            }}
            value={searchFieldValue}
            title="Recherche un point de vente"
            placeholder="Key, Nom, ..."
            type="search"
            unit={
              <SearchIcon
                style={{
                  color: themeColors.light
                }}
              />
            }
          />
        </Item>
        <Item xs={3} justify="flex-start">
          <MultipleSelectSearch
            title={shopGroupName}
            options={shopGroupsWithShopsOptions}
            values={selectedShopGroups}
            onChange={options => {
              setPageNumber(0);
              setSelectedShopGroups(options);
            }}
            small
          />
        </Item>
      </Row>
      <Row spacing={4}>
        <Item>
          <Loading loading={!loaded} />
          {loaded && (
            <TableData
              rows={rows}
              headers={[
                { label: 'Key', id: 'key', sortKey: 'key' },
                { label: shopName, id: 'name', sortKey: 'name' },
                {
                  label: shopGroupName,
                  id: 'shopGroups',
                  format: (value = []) => {
                    return formatShopGroups(
                      value.map(el => el.name),
                      shopGroupName,
                      'pml'
                    );
                  }
                }
              ]}
              hidenFields={['_id']}
              onUpdate={openUpdate}
              onDelete={openDelete}
              pageNumber={pageNumber}
              numberPerRow={limit}
              overrideCount={total}
              skipSlice
              getPageNumber={page => {
                setPageNumber(page);
              }}
              setNumberPerRow={newLimit => {
                setLimit(newLimit);
                setPageNumber(0);
              }}
            />
          )}
          {openModal === 'modalUpdate' && (
            <ModalForm
              open={openModal === 'modalUpdate'}
              onClose={closeModal}
              onChange={onUpdate}
              shopGroupName={shopGroupName}
              config={{ medias, users: [], selected, shopGroups }}
            />
          )}
          {openModal === 'modalDelete' && (
            <ModalDelete
              name={selected.name}
              type="ce point de vente"
              open={openModal === 'modalDelete'}
              onClose={closeModal}
              onDelete={onDelete}
            />
          )}
          {openModal === 'unauthorized' && (
            <UnauthorizedModal
              open={openModal === 'unauthorized'}
              onClose={closeModal}
            />
          )}
        </Item>
      </Row>
    </Container>
  );
};

export default ShopContainer;
